import Actions from 'reducers/Releases'
import cloneDeep from 'lodash/cloneDeep'
import { ReleasesService, AuthService } from 'services'
import Release from 'models/Release'
import { releasePageSize } from 'config'
import Song from 'models/Song'
import ReleaseActions from 'reducers/Releases'
import { forceLogout, isSessionInvalidResponse } from 'utils/authSession'
import type { AppDispatch } from 'types/store'
import { nanoid } from '@reduxjs/toolkit'
import { hasArchivedFilter } from 'utils/archiveFilter'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'

const refreshTokens = async (dispatch: AppDispatch) => {
  const currentRefreshToken = localStorage.getItem('refreshToken')

  if (!currentRefreshToken) throw new Error('No refresh token available')

  try {
    const { data } = await AuthService.refresh(currentRefreshToken)

    const { 'access-token': accessToken, 'refresh-token': newRefreshToken } =
      data

    if (!accessToken || !newRefreshToken) {
      await forceLogout(dispatch)
      throw new Error('Token refresh failed')
    }

    localStorage.setItem('accessToken', accessToken)
    localStorage.setItem('refreshToken', newRefreshToken)
  } catch (error) {
    if (isSessionInvalidResponse(error)) {
      await forceLogout(dispatch)
    }

    throw error
  }
}

const prepareSearchParams = (
  state: any,
  page: number,
  pagesize?: number,
  archiveToFilterEnabled = false
) => {
  const { releases: releasesState, options: optionsState } = state
  // Map filter types to API param keys
  const typeToParam: Record<string, string> = {
    genre: 'genres',
    market: 'markets',
    version: 'versions',
    release_type: 'release_types',
    package: 'packages',
    mood: 'moods',
    instrument: 'instruments'
  }
  // Build params from activeFilters
  const params: Record<string, any> = {
    page,
    pagesize: pagesize || releasePageSize,
    sort: releasesState.sort
  }
  if (Array.isArray(optionsState.activeFilters)) {
    Object.keys(typeToParam).forEach((type) => {
      const ids = optionsState.activeFilters
        .filter((f: any) => f.type === type)
        .map((f: any) => f.id)
      params[typeToParam[type]] = ids.length ? ids.join(',') : null
    })
  }
  params.explicit = optionsState.additionalFilterSettings?.explicit || null
  if (archiveToFilterEnabled) {
    params.archived = hasArchivedFilter(optionsState.activeFilters)
  }
  return params
}

export const search =
  (page: number, pageSize?: number, archiveToFilterEnabled?: boolean) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    // Capture the query before refreshing tokens; later UI changes must not
    // change the meaning of this request or allow its response to replace them.
    const state = getState()
    const enabled =
      archiveToFilterEnabled ?? state.releases.archiveToFilterEnabled ?? false
    const params = prepareSearchParams(state, page, pageSize, enabled)
    const queryKey = JSON.stringify({ ...params, page: undefined })
    if (state.releases.loading && state.releases.queryKey === queryKey) return
    if (
      page > 1 &&
      (state.releases.queryKey !== queryKey || page !== state.releases.page + 1)
    )
      return

    const requestId = nanoid()
    dispatch(
      Actions.requestStarted({
        queryKey,
        requestId,
        page,
        archiveToFilterEnabled: enabled,
        archived: Boolean(params.archived)
      })
    )

    try {
      await refreshTokens(dispatch)
      if (getState().releases.requestId !== requestId) return

      const response = await ReleasesService.search(params)

      const items = response.data.items.map((item: any) => {
        const result = cloneDeep(item)
        result.publisher = Release.getPublisher(item)
        if (enabled && typeof result.archived !== 'boolean') {
          result.archived = params.archived
        }
        return result
      })

      const totalItemsCount = response.data.total
      dispatch(
        Actions.requestSucceeded({
          requestId,
          releases: items,
          page,
          totalItemsCount
        })
      )
    } catch (error) {
      console.warn(error)
      dispatch(
        Actions.requestFailed({
          requestId,
          error: error instanceof Error ? error.message : 'Request failed'
        })
      )
    }
  }

export const refreshList =
  (archiveToFilterEnabled?: boolean) => (dispatch: AppDispatch) => {
    dispatch(Actions.refreshList())
    return dispatch(search(1, undefined, archiveToFilterEnabled) as any)
  }

export const sortBy = (attribute: string) => (dispatch: AppDispatch) => {
  dispatch(Actions.sortBy(attribute))
}

export const getDetail =
  (
    releaseId: string,
    successCallback: (result: any) => void = () => {},
    errorCallback: () => void = () => {}
  ) =>
  async (dispatch: AppDispatch) => {
    const { data } = await ReleasesService.getDetail(releaseId)
    const result = cloneDeep(data)

    dispatch(ReleaseActions.setCurrentReleaseInformation(data))

    result.songs = result.songs.map((item: any) => ({
      ...Song.addReleaseFields(item, result),
      releaseid: releaseId
    }))

    successCallback(result)
  }

export const archive =
  (
    releaseId: string,
    value: boolean,
    successCallback: (data?: any) => void = () => {},
    errorCallback: (() => void) | null = () => {},
    archiveToFilterEnabled = false
  ) =>
  async (dispatch: AppDispatch, getState: () => any) => {
    const id = String(releaseId)
    if (getState().releases.pendingArchiveIds.includes(id)) return
    const releasesState = getState().releases
    const release = [
      releasesState.currentReleaseInformation,
      releasesState.selectedReleaseInModal,
      ...releasesState.releases
    ].find((item) => item && String(item.releaseid) === id)
    const errorLabel = release
      ? [release.artist, release.title].filter(Boolean).join(' - ')
      : id
    dispatch(Actions.archiveStarted(id))
    try {
      const response = value
        ? await ReleasesService.archive(releaseId)
        : await ReleasesService.unArchive(releaseId)
      successCallback?.(response.data)
      if (archiveToFilterEnabled) {
        // Reload the current query, rather than inserting a restored release
        // that might not match its filters or sorting.
        await dispatch(
          refreshList(
            getState().releases.queryKey
              ? getState().releases.archiveToFilterEnabled
              : true
          ) as any
        )
      }
    } catch (error) {
      console.warn(error)
      errorCallback?.()
      if (archiveToFilterEnabled) {
        dispatch(
          NotificationActions.broadcastMessage(
            errorLabel,
            NotificationType.hasError,
            'error'
          )
        )
      }
    } finally {
      dispatch(Actions.archiveFinished(id))
    }
  }

export const genres = () => (dispatch: AppDispatch) => {
  dispatch(Actions.loadingGenres())
  ReleasesService.genres()
    .then((response: any) => {
      dispatch(Actions.loadingGenresSuccess(response.data))
    })
    .catch((error: any) => {
      console.warn(error)
      dispatch(Actions.loadingGenresFail(error))
    })
}

export const shareRelease =
  (releaseId: string, playersId: string[], message: string) => async () => {
    return ReleasesService.shareRelease(releaseId, playersId, message)
  }

export default {
  search,
  refreshList,
  sortBy,
  getDetail,
  archive,
  genres,
  refreshTokens,
  shareRelease
}
